import { type PropsWithChildren } from 'react';
import { ImageBackground } from 'expo-image';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { getContentfulImageUrl, theme } from '@sg/garnish';

import { useProductDetailsNavigationHeaderHeight } from '../../../ProductDetailsNavigationHeader';

// ─────────────────────────────────────────────────────────────────────────────

const ProductDetailsHeaderSmallOuterContainer = (props: OuterContainerProps) => {
  const { children, backgroundColor = theme.colors.KALE, backgroundImageBaseUrl } = props;

  const navHeaderHeight = useProductDetailsNavigationHeaderHeight();
  const { right: safeAreaRight, left: safeAreaLeft } = useSafeAreaInsets();

  // ─── Derived Data ────────────────────────────────────────────────────

  const imageSource = backgroundImageBaseUrl
    ? getContentfulImageUrl(backgroundImageBaseUrl, { w: 400, fit: 'fill', f: 'top' }) // prettier-ignore
    : undefined;

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(
    () => ({
      backgroundColor,
      paddingTop: navHeaderHeight,
      paddingRight: theme.spacing['6'] + safeAreaRight,
      paddingLeft: theme.spacing['6'] + safeAreaLeft,
    }),
    [backgroundColor, navHeaderHeight, safeAreaLeft, safeAreaRight],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={[styles.outerContainer, containerDynamicStyles]}>
      {imageSource ? (
        <ImageBackground source={imageSource} style={StyleSheet.absoluteFill} />
      ) : null}

      {children}
    </View>
  );
};

const ProductDetailsHeaderSmallContainer = (props: Props) => {
  const { children } = props;

  return <View style={styles.container}>{children}</View>;
};

const ProductDetailsHeaderSmallContentContainer = (props: ContentContainerProps) => {
  const { children, withMoreSpacing = false } = props;

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [
    styles.contentContainer,
    withMoreSpacing ? styles.contentContainerLarger : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return <View style={containerStyles}>{children}</View>;
};

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsHeaderSmall = {
  OuterContainer: ProductDetailsHeaderSmallOuterContainer,
  Container: ProductDetailsHeaderSmallContainer,
  ContentContainer: ProductDetailsHeaderSmallContentContainer,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    gap: theme.spacing['4'],
    paddingBottom: theme.spacing['6'],
  },
  container: {
    flexDirection: 'row',
    gap: theme.spacing['1'],
  },
  contentContainer: {
    flex: 1,
    paddingTop: theme.spacing['8'],
    gap: theme.spacing['6'],
  },
  contentContainerLarger: {
    paddingTop: theme.spacing['10'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren;

type OuterContainerProps = PropsWithChildren<{
  backgroundColor: string | undefined;
  backgroundImageBaseUrl: string | undefined;
}>;

type ContentContainerProps = PropsWithChildren<{
  withMoreSpacing?: boolean;
}>;
